import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { AppLoader } from 'components/Core/AppLoader';
import { EntityTitle } from 'components/Core/EntityTitle';
import TranslationsForMobileForm from 'components/Forms/TranslationsForMobileForm/TranslationsForMobileForm';
import PointCategoryBasicForm from 'components/Forms/PointCategoryBasicForm/PointCategoryBasicForm';
import ProductCategoryBasicForm from 'components/Forms/ProductCategoryBasicForm/ProductCategoryBasicForm';
import ProductCategoryKeywordsForm from 'components/Forms/ProductCategoryKeywordsForm/ProductCategoryKeywordsForm';
import { DEFAULT_CATEGORY_TAB } from 'stores/categories/constatnts';
import { CategoriesEntity, CategoriesTab } from 'stores/categories/types';
import { categoryTabs, productCategoryTabs } from './constants';
import { categoryTranslations } from './translations';
import { CategoryTransFields } from './types';
import styles from './category.module.scss';

const Category: FC = () => {
  const {
    categoriesStore: {
      pointsCategoryData,
      getPointsCategoryData,
      patchPointsCategoryTranslations,
      productCategoryData,
      getProductCategoryData,
      createProductCategoryKeyword,
      updateProductCategoryKeyword,
      deleteProductCategoryKeyword,
      patchProductCategoryTranslations,
      productCategories,
      getCategoriesData,
    },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<CategoryTransFields>([categoryTranslations.name]);

  const navigate = useNavigate();
  const { id, tab, entity } = useParams();

  const isProduct = entity === 'product-categories';
  const entityTabs = isProduct ? productCategoryTabs : categoryTabs;
  const defaultTab: CategoriesTab = isProduct ? 'basic' : DEFAULT_CATEGORY_TAB;

  const [currentTab, setCurrentTab] = useState<CategoriesTab>(entityTabs.find((item) => item === tab) || defaultTab);

  const { isFetching: isLoadingPoint } = useQuery(
    'getPointsCategoryData',
    () => (id ? getPointsCategoryData(id) : null),
    { retry: false, refetchOnWindowFocus: false, enabled: !isProduct && !!id }
  );

  const { isFetching: isLoadingProduct } = useQuery(
    'getProductCategoryData',
    () => (id ? getProductCategoryData(id) : null),
    { retry: false, refetchOnWindowFocus: false, enabled: isProduct && !!id }
  );

  // All product categories (with keywords) — used to warn about the same
  // keyword being assigned to more than one category (which dilutes the boost).
  useQuery('getProductCategoriesList', () => getCategoriesData('product-categories'), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isProduct,
  });

  const otherCategoriesKeywords = useMemo(
    () =>
      (productCategories || [])
        .filter((c) => c.id !== id)
        .map((c) => ({ categoryName: c.categoryName, keywords: c.keywords || [] })),
    [productCategories, id]
  );

  const isLoading = isProduct ? isLoadingProduct : isLoadingPoint;

  const { mutate: saveTranslations, isLoading: isSaving } = useMutation(patchPointsCategoryTranslations, {
    mutationKey: 'patchPointsCategoryTranslations',
    onSuccess: () => showAlert(t('translationsSaved'), 'success'),
  });

  const { mutateAsync: createKeyword, isLoading: isCreatingKeyword } = useMutation(
    (payload: { name: string; weight: number }) => createProductCategoryKeyword(payload),
    { mutationKey: 'createProductCategoryKeyword', onSuccess: () => showAlert(t('keywordsSaved'), 'success') }
  );
  const { mutateAsync: updateKeyword, isLoading: isUpdatingKeyword } = useMutation(
    (vars: { id: string; payload: { name?: string; weight?: number } }) =>
      updateProductCategoryKeyword(vars.id, vars.payload),
    { mutationKey: 'updateProductCategoryKeyword', onSuccess: () => showAlert(t('keywordsSaved'), 'success') }
  );
  const { mutateAsync: deleteKeyword, isLoading: isDeletingKeyword } = useMutation(
    (keywordId: string) => deleteProductCategoryKeyword(keywordId),
    { mutationKey: 'deleteProductCategoryKeyword', onSuccess: () => showAlert(t('keywordDeleted'), 'success') }
  );
  const isSavingKeywords = isCreatingKeyword || isUpdatingKeyword || isDeletingKeyword;

  const { mutate: saveProductTranslations, isLoading: isSavingProductTr } = useMutation(
    patchProductCategoryTranslations,
    {
      mutationKey: 'patchProductCategoryTranslations',
      onSuccess: () => showAlert(t('translationsSaved'), 'success'),
    }
  );

  const onChangeTab = (nextTab: CategoriesTab) => {
    if (nextTab !== currentTab) {
      navigate(`/categories/${entity}/${id}/${nextTab}`);
      setCurrentTab(nextTab);
    }
  };

  const title = isProduct ? productCategoryData?.categoryName : pointsCategoryData?.categoryName;

  const component = useMemo(() => {
    if (!entity || !id) return null;
    switch (currentTab) {
      case 'basic':
        if (isProduct) {
          return <ProductCategoryBasicForm />;
        }
        if (entity === 'points-categories') {
          return <PointCategoryBasicForm />;
        }
        return null;
      case 'translations':
        return (
          <TranslationsForMobileForm
            title={t('translations')}
            translations={(isProduct ? productCategoryData?.translations : pointsCategoryData?.translations) || {}}
            onSave={isProduct ? saveProductTranslations : saveTranslations}
            isLoading={isProduct ? isSavingProductTr : isSaving}
            // Point categories are applied to every country by the backend (MOB1-2123).
            saveHint={isProduct ? undefined : t('allCountriesHint')}
            confirm={
              isProduct
                ? undefined
                : { title: t('confirmTitle'), description: t('confirmText'), buttonLabel: t('confirm') }
            }
          />
        );
      case 'keywords':
        return (
          <ProductCategoryKeywordsForm
            keywords={productCategoryData?.keywordItems || []}
            categoryName={productCategoryData?.categoryName}
            otherCategories={otherCategoriesKeywords}
            onCreate={(payload) => createKeyword(payload)}
            onUpdate={(id, payload) => updateKeyword({ id, payload })}
            onDelete={(id) => deleteKeyword(id)}
            isLoading={isSavingKeywords}
          />
        );
      default:
        return null;
    }
  }, [
    currentTab,
    entity,
    id,
    isProduct,
    pointsCategoryData,
    productCategoryData,
    isSaving,
    isSavingKeywords,
    isSavingProductTr,
    saveTranslations,
    saveProductTranslations,
    createKeyword,
    updateKeyword,
    deleteKeyword,
    t,
  ]);

  return (
    <main className={styles.page}>
      <EntityTitle title={`${t(entity as CategoriesEntity)}: ${title || ''}`} />
      <div className={styles.container}>
        <div className={styles.filters}>
          {entityTabs.map((item) => (
            <button
              key={item}
              className={`${styles.filterBtn}  ${currentTab === item && styles.filterBtnCurent}`}
              type='button'
              onClick={() => onChangeTab(item)}
              disabled={isLoading || !id}>
              <span className={styles.btnTitle}>{t(item)}</span>
            </button>
          ))}
        </div>
        {isLoading ? <AppLoader /> : component}
      </div>
    </main>
  );
};

export default observer(Category);
